import classNames from 'classnames';
import {
  ImageWithTextSideBySideSlice,
  ImageWithTextSideBySideSliceWithImageCarouselVariation,
} from 'prismicio-types';
import { CSSProperties, FC } from 'react';
import { asText, ImageField } from '@prismicio/client';
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css/effect-fade';

import { PrismicRichText } from '@prismicio/react';
import {
  getContentAlign,
  getContentVerticalAlign,
  HeadingAsParagraph,
  htmlSerializerWithProps,
  iwtsPrimaryFields,
  toCtaButtonPropsFromItem,
  withIwtsFitContent,
} from '@/utils';
import { CtaButton, Link, PrismicImageWithAlt, Slider } from '@/components';
import { useFilteredSliceItemsByLocationSlug } from '@/hooks';
import { resolveImageRadiusToken } from '../../../theme/tokens';

import {
  getIwtsIconTextOrderStyle,
  getIwtsStandaloneCtaClassName,
} from '../ImageWithTextSideBySide.components';
import sharedStyle from '../components.module.scss';
import style from './withImageCarouselVariation.module.scss';

export const WithImageCarouselVariation: FC<{
  slice: ImageWithTextSideBySideSliceWithImageCarouselVariation &
    ImageWithTextSideBySideSlice;
}> = ({ slice }) => {
  const { filteredItems } = useFilteredSliceItemsByLocationSlug(slice?.items);
  const {
    content_background_color,
    content_horizontal_align,
    content_vertical_align,
    content_full_width,
    description,
    description_color,
    heading,
    heading_color,
    icon_as_heading,
    icon_as_heading_max_width,
    layout_direction,
    title,
    title_color,
    autoplay_image_carousel,
    section_background_color,
  } = slice.primary;
  const imageCornerPreset =
    'image_corner_preset' in slice.primary
      ? slice.primary.image_corner_preset
      : 'brand';

  const layoutDirection = layout_direction ?? 'left';
  const alignText = content_horizontal_align ?? 'center';
  const hasContentBackground = !!content_background_color;
  const isFullWidth = Boolean(content_full_width);
  const horizontalAlign = getContentAlign(
    content_horizontal_align as 'start' | 'left' | 'center' | 'end' | 'right',
  );
  const verticalAlign = getContentVerticalAlign(content_vertical_align);
  const primaryCta = withIwtsFitContent(
    toCtaButtonPropsFromItem(slice.primary, iwtsPrimaryFields, 'primary'),
    slice.primary,
    slice.variation,
  );

  const renderImage = (image?: ImageField) => {
    return (
      <figure
        className={classNames(
          style.image,
          {
            [style.image_full_width_left]:
              isFullWidth && layoutDirection === 'left',
            [style.image_full_width_right]:
              isFullWidth && layoutDirection === 'right',
          },
          resolveImageRadiusToken(imageCornerPreset as string),
          {
            [style.image__default]: !image?.url,
          },
        )}
      >
        <PrismicImageWithAlt
          field={image}
          height={image?.dimensions?.height}
          showPlaceholderIfEmpty
          style={{
            objectFit: 'contain',
            width: '100%',
            height: 'auto',
            display: 'block',
          }}
          width={image?.dimensions?.width}
        />
      </figure>
    );
  };

  return (
    <>
      <div className={style.variation_carousel_media}>
        {filteredItems?.length > 0 ? (
          <Slider
            autoplay={
              autoplay_image_carousel
                ? {
                    delay: 5000,
                    disableOnInteraction: false,
                    stopOnLastSlide: false,
                  }
                : false
            }
            effect="fade"
            fadeEffect={{ crossFade: true }}
            loop={filteredItems.length > 1}
            modules={[Autoplay, EffectFade, Navigation, Pagination]}
            pagination={{
              clickable: true,
              renderBullet: (index, className) =>
                `<span class="${className}" role="button" tabindex="0" aria-label="Go to slide ${
                  index + 1
                }"></span>`,
            }}
            showSliderButtons={true}
            showSliderHeader={false}
            showSlidesOutsideContainer
            slidesPerView={1}
            spaceBetween={0}
          >
            {filteredItems?.map((item, index) => {
              return (
                <Link
                  className={style.variation_carousel_slide}
                  href={item?.image_link ?? '/'}
                  key={index}
                  style={{ background: section_background_color ?? undefined }}
                >
                  {renderImage(item?.image)}
                </Link>
              );
            })}
          </Slider>
        ) : (
          renderImage()
        )}
      </div>
      <div
        className={classNames(style.body, {
          [style.body__left]: layoutDirection === 'left',
          [style.body__right]: layoutDirection === 'right',
          [style.body__full_left]:
            content_full_width && layoutDirection === 'left',
          [style.body__full_right]:
            content_full_width && layoutDirection === 'right',
        })}
      >
        <div
          className={classNames(
            style.content,
            {
              [style.content_with_background]: hasContentBackground,
            },
            hasContentBackground &&
              resolveImageRadiusToken(imageCornerPreset as string),
          )}
          style={
            {
              alignItems: horizontalAlign,
              textAlign: alignText,
              justifyContent: verticalAlign,
              background: content_background_color as string,
              '--cta-align': horizontalAlign,
            } as CSSProperties
          }
        >
          {(icon_as_heading?.url || asText(heading)) && (
            <div
              className={style.body__heading_container}
              style={getIwtsIconTextOrderStyle(alignText)}
            >
              {icon_as_heading?.url && (
                <figure
                  className={style.body__heading_icon}
                  style={{ maxWidth: icon_as_heading_max_width ?? '' }}
                >
                  <PrismicImageWithAlt
                    defaultAlt="Decoration icon"
                    field={icon_as_heading}
                    height={icon_as_heading?.dimensions?.height}
                    style={{
                      objectFit: 'contain',
                      width: '100%',
                      height: 'auto',
                    }}
                    width={icon_as_heading?.dimensions?.width}
                  />
                </figure>
              )}
              <HeadingAsParagraph
                className={style.body__text_heading}
                color={heading_color as string}
                field={heading}
              />
            </div>
          )}
          {asText(title) && (
            <div
              className={style.body__title_container}
              style={getIwtsIconTextOrderStyle(alignText)}
            >
              <PrismicRichText
                components={htmlSerializerWithProps(
                  {
                    color: title_color,
                  },
                  style.body__title,
                )}
                field={title}
              />
            </div>
          )}
          {asText(description) && (
            <div className={sharedStyle.body__description_text}>
              <PrismicRichText
                components={htmlSerializerWithProps({
                  color: description_color,
                })}
                field={description}
              />
            </div>
          )}
          {primaryCta && (
            <CtaButton
              {...primaryCta}
              className={getIwtsStandaloneCtaClassName({
                isLink: primaryCta.variant === 'link',
                fitContent: Boolean(primaryCta.fitContent),
              })}
              fitContent={Boolean(primaryCta.fitContent)}
            />
          )}
        </div>
      </div>
    </>
  );
};
